import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { instituteService } from '../../../services/instituteService';

const RANKING_TYPES = ['NIRF', 'QS World', 'Times Higher Education', 'NIRF Innovation', 'India Today', 'Outlook', 'IIRF', 'Other'];
const ACCREDITATION_OPTIONS = ['NAAC A++', 'NAAC A+', 'NAAC A', 'NAAC B++', 'NAAC B+', 'NAAC B', 'NBA', 'AICTE', 'UGC', 'AACSB', 'AMBA', 'EQUIS', 'ISO 9001', 'BCI', 'MCI', 'PCI', 'COA'];

const FeaturedInstitutesManager = () => {
  const [allInstitutes, setAllInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    ranking: '',
    state: '',
    feesMin: '',
    feesMax: '',
    rankingsData: [],
    accreditations: [],
    awards: ''
  });

  useEffect(() => {
    loadInstitutes();
  }, []);

  const loadInstitutes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await instituteService?.getAllInstitutesForAdmin({ approvalStatus: 'approved' });
      setAllInstitutes(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load institutes');
    } finally {
      setLoading(false);
    }
  };

  const featuredInstitutes = allInstitutes?.filter(i => i?.isFeatured);
  const canFeatureMore = featuredInstitutes?.length < 8;

  const handleToggleFeatured = async (institute) => {
    const newValue = !institute?.isFeatured;
    if (newValue && !canFeatureMore) {
      alert('You can only feature up to 8 institutes. Please unfeature one first.');
      return;
    }
    try {
      setSaving(institute?.id);
      await instituteService?.setFeaturedInstitute(institute?.id, newValue);
      setAllInstitutes(prev => prev?.map(i =>
        i?.id === institute?.id ? { ...i, isFeatured: newValue } : i
      ));
    } catch (err) {
      alert(err?.message || 'Failed to update featured status');
    } finally {
      setSaving(null);
    }
  };

  const handleEditOpen = (institute) => {
    setEditingId(institute?.id);
    setEditForm({
      ranking: institute?.ranking || '',
      state: institute?.state || institute?.user?.state || '',
      feesMin: institute?.feesMin || '',
      feesMax: institute?.feesMax || '',
      rankingsData: institute?.rankingsData?.length > 0 ? institute?.rankingsData : [],
      accreditations: institute?.accreditations || [],
      awards: institute?.awards || ''
    });
  };

  const handleEditSave = async (instituteId) => {
    try {
      setSaving(instituteId);
      await instituteService?.updateInstituteRankingAndState(instituteId, {
        ranking: editForm?.ranking,
        state: editForm?.state,
        feesMin: editForm?.feesMin,
        feesMax: editForm?.feesMax,
        rankingsData: editForm?.rankingsData,
        accreditations: editForm?.accreditations,
        awards: editForm?.awards
      });
      setAllInstitutes(prev => prev?.map(i =>
        i?.id === instituteId ? {
          ...i,
          ranking: editForm?.ranking ? Number(editForm?.ranking) : null,
          state: editForm?.state || null,
          feesMin: editForm?.feesMin ? Number(editForm?.feesMin) : null,
          feesMax: editForm?.feesMax ? Number(editForm?.feesMax) : null,
          rankingsData: editForm?.rankingsData,
          accreditations: editForm?.accreditations,
          awards: editForm?.awards || null
        } : i
      ));
      setEditingId(null);
    } catch (err) {
      alert(err?.message || 'Failed to save changes');
    } finally {
      setSaving(null);
    }
  };

  // Rankings data helpers
  const handleAddRanking = () => {
    setEditForm(f => ({ ...f, rankingsData: [...(f?.rankingsData || []), { type: 'NIRF', rank: '' }] }));
  };

  const handleRankingChange = (index, field, value) => {
    setEditForm(f => {
      const updated = [...(f?.rankingsData || [])];
      updated[index] = { ...updated?.[index], [field]: value };
      return { ...f, rankingsData: updated };
    });
  };

  const handleRemoveRanking = (index) => {
    setEditForm(f => ({ ...f, rankingsData: f?.rankingsData?.filter((_, i) => i !== index) }));
  };

  // Accreditation helpers
  const handleToggleAccreditation = (acc) => {
    setEditForm(f => {
      const current = f?.accreditations || [];
      const exists = current?.includes(acc);
      return {
        ...f,
        accreditations: exists ? current?.filter(a => a !== acc) : [...current, acc]
      };
    });
  };

  const filtered = allInstitutes?.filter(i =>
    i?.instituteName?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700 mb-3">{error}</p>
        <Button onClick={loadInstitutes} iconName="RefreshCw" iconPosition="left" size="sm">Retry</Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground">Featured Institutes Manager</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Curate up to <span className="font-semibold text-primary">8 featured institutes</span> shown at the top of the Exhibition Hall.
            Also set rankings, accreditations, awards, state, and fee range.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 px-4 py-2 rounded-full">
          <Icon name="Star" size={16} color="#d97706" />
          <span className="text-sm font-bold text-yellow-700">{featuredInstitutes?.length} / 8 Featured</span>
        </div>
      </div>

      {/* Featured slots preview */}
      {featuredInstitutes?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
          <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide mb-3">Currently Featured</p>
          <div className="flex flex-wrap gap-2">
            {featuredInstitutes?.map(i => (
              <div key={i?.id} className="flex items-center gap-2 bg-white border border-yellow-300 rounded-full px-3 py-1.5 text-sm">
                <Icon name="Star" size={12} color="#d97706" />
                <span className="font-medium text-foreground">{i?.instituteName}</span>
                <button
                  onClick={() => handleToggleFeatured(i)}
                  className="text-muted-foreground hover:text-red-500 transition-colors ml-1"
                  disabled={saving === i?.id}
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          placeholder="Search institutes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
      </div>

      {/* Institute list */}
      <div className="space-y-3 max-h-[700px] overflow-y-auto pr-1">
        {filtered?.map(institute => (
          <div key={institute?.id} className={`bg-white border rounded-xl p-4 transition-all ${institute?.isFeatured ? 'border-yellow-300 bg-yellow-50/30' : 'border-border'}`}>
            <div className="flex items-start gap-4">
              {/* Logo */}
              <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={institute?.logoUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e7bb1524-1766485632262.png'}
                  alt={`${institute?.instituteName} logo`}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-foreground text-sm">{institute?.instituteName}</h3>
                  {institute?.isFeatured && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700 font-medium">
                      <Icon name="Star" size={10} color="#d97706" /> Featured
                    </span>
                  )}
                  {institute?.ranking && (
                    <span className="text-xs text-muted-foreground">#{institute?.ranking} NIRF</span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                  {(institute?.state || institute?.user?.state) && (
                    <span className="flex items-center gap-1">
                      <Icon name="MapPin" size={11} /> {institute?.state || institute?.user?.state}
                    </span>
                  )}
                  {institute?.categories?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Icon name="Tag" size={11} /> {institute?.categories?.slice(0, 2)?.join(', ')}
                      {institute?.categories?.length > 2 && ` +${institute?.categories?.length - 2}`}
                    </span>
                  )}
                  {institute?.feesMin && institute?.feesMax && (
                    <span className="flex items-center gap-1">
                      <Icon name="IndianRupee" size={11} /> ₹{(institute?.feesMin / 100000)?.toFixed(1)}L–₹{(institute?.feesMax / 100000)?.toFixed(1)}L/yr
                    </span>
                  )}
                  {institute?.accreditations?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Icon name="Award" size={11} /> {institute?.accreditations?.slice(0, 2)?.join(', ')}
                      {institute?.accreditations?.length > 2 && ` +${institute?.accreditations?.length - 2}`}
                    </span>
                  )}
                </div>

                {/* Expanded Edit Form */}
                {editingId === institute?.id && (
                  <div className="mt-4 space-y-4 border-t border-border pt-4">

                    {/* Basic Info Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block font-medium">NIRF Ranking</label>
                        <input
                          type="number"
                          placeholder="e.g. 12"
                          value={editForm?.ranking}
                          onChange={(e) => setEditForm(f => ({ ...f, ranking: e?.target?.value }))}
                          className="w-full border border-border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block font-medium">State</label>
                        <input
                          type="text"
                          placeholder="e.g. Tamil Nadu"
                          value={editForm?.state}
                          onChange={(e) => setEditForm(f => ({ ...f, state: e?.target?.value }))}
                          className="w-full border border-border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block font-medium">Min Fees (₹/yr)</label>
                        <input
                          type="number"
                          placeholder="e.g. 100000"
                          value={editForm?.feesMin}
                          onChange={(e) => setEditForm(f => ({ ...f, feesMin: e?.target?.value }))}
                          className="w-full border border-border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block font-medium">Max Fees (₹/yr)</label>
                        <input
                          type="number"
                          placeholder="e.g. 500000"
                          value={editForm?.feesMax}
                          onChange={(e) => setEditForm(f => ({ ...f, feesMax: e?.target?.value }))}
                          className="w-full border border-border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                    </div>

                    {/* Rankings Section */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-semibold text-foreground flex items-center gap-1">
                          <Icon name="TrendingUp" size={13} /> Rankings (NIRF, QS World, etc.)
                        </label>
                        <button
                          type="button"
                          onClick={handleAddRanking}
                          className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 font-medium"
                        >
                          <Icon name="Plus" size={12} /> Add Ranking
                        </button>
                      </div>
                      {editForm?.rankingsData?.length === 0 && (
                        <p className="text-xs text-muted-foreground italic">No rankings added yet. Click "Add Ranking" to add one.</p>
                      )}
                      <div className="space-y-2">
                        {editForm?.rankingsData?.map((r, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <select
                              value={r?.type || 'NIRF'}
                              onChange={(e) => handleRankingChange(idx, 'type', e?.target?.value)}
                              className="border border-border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary bg-white flex-1"
                            >
                              {RANKING_TYPES?.map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                            <input
                              type="number"
                              placeholder="Rank #"
                              value={r?.rank || ''}
                              onChange={(e) => handleRankingChange(idx, 'rank', e?.target?.value)}
                              className="border border-border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary w-24"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveRanking(idx)}
                              className="text-red-400 hover:text-red-600 transition-colors p-1"
                            >
                              <Icon name="X" size={13} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Accreditations Section */}
                    <div>
                      <label className="text-xs font-semibold text-foreground flex items-center gap-1 mb-2">
                        <Icon name="ShieldCheck" size={13} /> Accreditations
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {ACCREDITATION_OPTIONS?.map(acc => {
                          const selected = editForm?.accreditations?.includes(acc);
                          return (
                            <button
                              key={acc}
                              type="button"
                              onClick={() => handleToggleAccreditation(acc)}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                                selected
                                  ? 'bg-primary text-white border-primary' :'bg-white text-muted-foreground border-border hover:border-primary hover:text-primary'
                              }`}
                            >
                              {acc}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Awards Section */}
                    <div>
                      <label className="text-xs font-semibold text-foreground flex items-center gap-1 mb-1">
                        <Icon name="Trophy" size={13} /> Awards & Recognitions
                      </label>
                      <textarea
                        placeholder="e.g. Best Engineering College 2024 by Education World, NIRF Innovation Award..."
                        value={editForm?.awards}
                        onChange={(e) => setEditForm(f => ({ ...f, awards: e?.target?.value }))}
                        rows={2}
                        className="w-full border border-border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                      />
                    </div>

                    {/* Save / Cancel */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleEditSave(institute?.id)}
                        loading={saving === institute?.id}
                        iconName="Check"
                        iconPosition="left"
                      >
                        Save Changes
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => handleEditOpen(institute)}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                  title="Edit ranking & details"
                >
                  <Icon name="Edit2" size={15} />
                </button>
                <button
                  onClick={() => handleToggleFeatured(institute)}
                  disabled={saving === institute?.id || (!institute?.isFeatured && !canFeatureMore)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    institute?.isFeatured
                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border border-yellow-300'
                      : canFeatureMore
                        ? 'bg-gray-100 text-gray-600 hover:bg-yellow-50 hover:text-yellow-700 border border-border' :'bg-gray-50 text-gray-400 cursor-not-allowed border border-border'
                  }`}
                >
                  {saving === institute?.id ? (
                    <Icon name="Loader" size={12} className="animate-spin" />
                  ) : (
                    <Icon name="Star" size={12} color={institute?.isFeatured ? '#d97706' : 'currentColor'} />
                  )}
                  {institute?.isFeatured ? 'Unfeature' : 'Feature'}
                </button>
              </div>
            </div>
          </div>
        ))}

        {filtered?.length === 0 && (
          <div className="text-center py-10 text-muted-foreground">
            <Icon name="Search" size={32} className="mx-auto mb-2 opacity-40" />
            <p>No institutes found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturedInstitutesManager;
